/**
 * Visitor Tracker - Client-side source attribution
 * Captures UTM params, referrer, device info and sends to backend
 */

import api from './api'

const VISITOR_KEY = 'cwd_labs_visitor_id'
const SESSION_KEY = 'cwd_labs_session_id'
const SOURCE_KEY = 'cwd_labs_source'

// Generate unique ID
function generateId() {
  return Date.now().toString(36) + Math.random().toString(36).substring(2, 10)
}

// Get or create visitor ID (persists across sessions)
function getVisitorId() {
  let id = localStorage.getItem(VISITOR_KEY)
  if (!id) {
    id = 'v_' + generateId()
    localStorage.setItem(VISITOR_KEY, id)
  }
  return id
}

// Get or create session ID (new per browser session)
function getSessionId() {
  let id = sessionStorage.getItem(SESSION_KEY)
  if (!id) {
    id = 's_' + generateId()
    sessionStorage.setItem(SESSION_KEY, id)
  }
  return id
}

// Parse URL params
function getUrlParams() {
  const params = new URLSearchParams(window.location.search)
  return {
    utm_source: params.get('utm_source') || '',
    utm_medium: params.get('utm_medium') || '',
    utm_campaign: params.get('utm_campaign') || '',
    utm_term: params.get('utm_term') || '',
    utm_content: params.get('utm_content') || '',
    gclid: params.get('gclid') || '',
    fbclid: params.get('fbclid') || '',
    ttclid: params.get('ttclid') || '',
    sccid: params.get('sccid') || '',
    msclkid: params.get('msclkid') || '',
    rdt_cid: params.get('rdt_cid') || ''
  }
}

// Track visitor on page load
export async function trackVisit() {
  try {
    const visitorId = getVisitorId()
    const sessionId = getSessionId()
    const urlParams = getUrlParams()

    const payload = {
      visitorId,
      sessionId,
      ...urlParams,
      landing_page: window.location.pathname + window.location.search,
      referrer: document.referrer || '',
      screenResolution: `${window.screen.width}x${window.screen.height}`
    }

    const res = await api.post('/track/visit', payload)

    if (res.data?.success && res.data?.data) {
      // Store source info for checkout attribution
      const sourceData = {
        source_type: res.data.data.source_type,
        source_platform: res.data.data.source_platform,
        visitorId,
        ...urlParams,
        landing_page: payload.landing_page,
        referrer: payload.referrer
      }
      localStorage.setItem(SOURCE_KEY, JSON.stringify(sourceData))
    }
  } catch (err) {
    // Silent fail - don't break the user experience
    console.debug('[Tracker] Error:', err.message)
  }
}

// Get stored source data for order attribution
export function getSourceData() {
  try {
    const stored = localStorage.getItem(SOURCE_KEY)
    if (stored) return JSON.parse(stored)
  } catch {}
  return {
    source_type: 'Direct',
    source_platform: 'Direct',
    visitorId: getVisitorId(),
    landing_page: '',
    referrer: ''
  }
}

// Track conversion (called after successful order)
export async function trackConversion(orderId, orderAmount) {
  try {
    const visitorId = getVisitorId()
    await api.post('/track/convert', { visitorId, orderId, orderAmount })
  } catch (err) {
    console.debug('[Tracker] Convert error:', err.message)
  }
}

export default { trackVisit, getSourceData, trackConversion }
